import { useState, useEffect } from 'react';
import { useIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import { Modal, message } from 'antd';
import { EMI } from 'shared/model/types';
import { getEMIAccountMeta } from 'shared/config/store/reducers/accountsSlice';
import { RootState } from 'shared/config/store/store';
import { getUserMeta } from 'shared/config/store/reducers/userSlice';
import { VerificationStatus } from 'entities/user/model/types';
import { useLazyGetAccountUserMetaQuery } from 'entities/account/api';
import { OnfidoVerification } from 'features/account/add-account/ui/OnfidoVerification';
import VerificationStatusModal from 'pages/dashboard/components/VerificationStatusModal';

type VerificationWidgetProps = {
  emi: EMI;
};

const VerificationWidget: React.FC<VerificationWidgetProps> = ({ emi }) => {
  const { formatMessage: t } = useIntl();
  const user = useSelector(getUserMeta);
  const emiMeta = useSelector((state: RootState) => getEMIAccountMeta(state, emi));
  const [getAccountUserMeta] = useLazyGetAccountUserMetaQuery();

  const [onfidoModalOpen, setOnfidoModalOpen] = useState(false);
  const [hasOpenedOnfido, setHasOpenedOnfido] = useState(false);
  const [statusModalOpen, setStatusModalOpen] = useState(false);
  const [hasOpenedStatusModal, setHasOpenedStatusModal] = useState(false);

  const isClientCreated = Object.keys(user.emiStatuses).includes(emi);
  const verificationStatus = emiMeta?.kyc_status;

  // Модалку Onfido открываем только если статус Init
  const shouldOpenOnfido = verificationStatus === VerificationStatus.Init;

  // Модалку статуса показываем для Pending или Rejected
  const shouldShowStatusModal =
    verificationStatus === VerificationStatus.Pending ||
    verificationStatus === VerificationStatus.Rejected;

  // Автоматически открываем модалку Onfido при статусе Init
  useEffect(() => {
    if (isClientCreated && shouldOpenOnfido && !hasOpenedOnfido && emiMeta) {
      setOnfidoModalOpen(true);
      setHasOpenedOnfido(true);
    }
  }, [isClientCreated, shouldOpenOnfido, hasOpenedOnfido, emiMeta]);

  // Автоматически показываем модалку статуса при Pending или Rejected
  useEffect(() => {
    if (isClientCreated && shouldShowStatusModal && !hasOpenedStatusModal && emiMeta) {
      setStatusModalOpen(true);
      setHasOpenedStatusModal(true);
    }
  }, [isClientCreated, shouldShowStatusModal, hasOpenedStatusModal, emiMeta]);

  const handleOnfidoComplete = async () => {
    setOnfidoModalOpen(false);
    try {
      await getAccountUserMeta({ emi }).unwrap();
      message.success(
        t({ id: 'dashboard.verification.completed' }) || 'Verification completed successfully'
      );
    } catch (error) {
      console.error('Failed to complete verification process:', error);
      message.error(t({ id: 'dashboard.verification.error' }) || 'Failed to complete verification');
    }
  };

  const handleCloseOnfidoModal = () => {
    setOnfidoModalOpen(false);
    setHasOpenedOnfido(true);
  };

  const handleCloseStatusModal = () => {
    setStatusModalOpen(false);
    setHasOpenedStatusModal(true);
  };

  const handleRetryVerification = () => {
    setStatusModalOpen(false);
    setHasOpenedStatusModal(true);
    setOnfidoModalOpen(true);
  };

  return (
    <>
      <VerificationStatusModal
        status={verificationStatus || VerificationStatus.Init}
        open={statusModalOpen}
        onClose={handleCloseStatusModal}
        onAction={
          verificationStatus === VerificationStatus.Rejected
            ? handleRetryVerification
            : undefined
        }
        actionText={
          verificationStatus === VerificationStatus.Rejected
            ? t({ id: 'verification.retry.buttonLabel' }) || 'Retry Verification'
            : undefined
        }
      />

      <Modal
        open={onfidoModalOpen}
        footer={null}
        centered
        onCancel={handleCloseOnfidoModal}
        closable={true}
        mask={true}
        zIndex={1001}
        getContainer={() => document.body}
        width={600}
      >
        <OnfidoVerification
          emi={emi}
          onComplete={handleOnfidoComplete}
          closeVerificationModal={handleCloseOnfidoModal}
        />
      </Modal>
    </>
  );
};

export default VerificationWidget;

